import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// v2 styles ship in the lazy v2 chunk via this component (the single-app model
// dynamically imports AllClaimsV2App); there is no separate v2 app-entry to load
// them. Without this import the v2 sidenav and form-nav button styles won't load.
import '../sass/all-claims-v2.scss';

import { focusElement } from 'platform/utilities/ui';
import { scrollToTop } from 'platform/utilities/scroll';
import { WIZARD_STATUS_COMPLETE } from 'platform/site-wide/wizard';
import {
  LOAD_STATUSES,
  PREFILL_STATUSES,
  SAVE_STATUSES,
} from 'platform/forms/save-in-progress/actions';
import RoutedSavableApp from 'platform/forms/save-in-progress/RoutedSavableApp';
import { RequiredLoginView } from '@department-of-veterans-affairs/platform-user/RequiredLoginView';
import backendServices from '@department-of-veterans-affairs/platform-user/profile/backendServices';
import environment from '@department-of-veterans-affairs/platform-utilities/environment';

import formConfig from '../config/form';
import { PAGE_TITLE_SUFFIX } from '../constants';
import { isBDD } from '../utils/bdd';
import { getWizardStatus } from '../../shared/utils/entryFlow';
import wrapWithBreadcrumb from '../utils/wrapWithBreadcrumb';
import AddPerson from '../../shared/containers/AddPerson';
import { Missing526Identifiers } from '../../shared/containers/Missing526Identifiers';
import { listMissingIdentifiers } from '../../shared/utils/missingIdentifiers';
import useEntryGateTracking from '../../shared/hooks/useEntryGateTracking';
import AllClaimsV2SideNav from './AllClaimsV2SideNav';
// Reuse the live 526 ITF check so new-flow forms behave like the old app: an
// Intent to File is fetched/created before the Veteran works the form.
import ITFWrapper from '../../all-claims/containers/ITFWrapper';

// The side nav is only useful on the actual form pages, not the intro,
// confirmation, or save-in-progress helper routes.
const HIDE_SIDENAV_SEGMENTS = [
  'introduction',
  'confirmation',
  'form-saved',
  'error',
  'resume',
];

// ITF is only fetched/created on real form pages — never on the intro,
// confirmation, or the SIP helper routes, which mount RoutedSavableApp to
// redirect/load and must not be blocked by the ITF loading state.
const NO_ITF_PAGES = [
  /\/introduction/,
  /\/confirmation/,
  /\/resume/,
  /\/form-saved/,
  /\/error/,
];

// 526 is an authenticated, identity-verified flow. RequiredLoginView redirects
// signed-out or unverified users to sign-in before any page renders.
const SERVICE_REQUIRED = [
  backendServices.FORM526,
  backendServices.ORIGINAL_CLAIMS,
];

// v2 render wrapper. The single-app version router (all-claims/routes.jsx +
// versioning.js) has already decided v1-vs-v2 before this mounts, so there is
// no version gating or cross-app redirect here — it just renders the v2 form
// (with the shared ITF check and side nav) behind the sign-in gate.
export function AllClaimsV2App({
  children,
  formData,
  formLoading,
  itfMessageDismissed,
  location,
  router,
  user,
}) {
  const pathname = (location?.pathname || '').replace(/\/+$/, '');
  const lastSegment = pathname.split('/').pop();
  const isMidFormPage =
    pathname !== '' && !HIDE_SIDENAV_SEGMENTS.includes(lastSegment);

  // A cold load (hard refresh or deep link) can only be this app's first render;
  // in-app navigation afterward never remounts it. So a mid-form page on the
  // first render means there's no in-progress form in memory yet.
  const isFirstRender = useRef(true);
  const bounceToIntro = isFirstRender.current && isMidFormPage;

  // Send that cold load to the intro's resume/start-over choice (same as the live
  // v1 app), rendering a spinner meanwhile rather than flashing the data-less
  // form or mounting ITFWrapper's ITF fetch. Keyed off the initial mount, not
  // currentlyLoggedIn, because the versioned router only selects v2 once logged
  // in — so v1's logged-out redirect window never happens here.
  useEffect(() => {
    isFirstRender.current = false;
    if (bounceToIntro) {
      router.replace('/introduction');
    }
    // Runs on mount; re-runs are harmless no-ops since bounceToIntro is only
    // ever true on the first render and the redirect is guarded by it.
  }, [bounceToIntro, router]);

  const wizardStatus = getWizardStatus();
  useEffect(() => {
    if (
      wizardStatus === WIZARD_STATUS_COMPLETE &&
      lastSegment === 'introduction'
    ) {
      focusElement('h1');
      scrollToTop();
    }
  }, [lastSegment, wizardStatus]);

  const title = formConfig.title({ formData });
  const breadcrumbTitle =
    lastSegment === 'introduction' ? `${title} ${PAGE_TITLE_SUFFIX}` : title;

  // These Veterans fail RequiredLoginView's service check, so handle them first
  // (same gates as v1's Form526EZApp).
  const profile = user?.profile || {};
  useEntryGateTracking({
    loggedIn: Boolean(user?.login?.currentlyLoggedIn),
    profile,
    formId: formConfig.formId,
    getIsBdd: () => isBDD(formData),
  });
  if (user?.login?.currentlyLoggedIn) {
    // "add-person": has EDIPI and SSN but is missing a BIRLS or participant ID.
    if (profile.services?.includes('add-person')) {
      return wrapWithBreadcrumb(
        breadcrumbTitle,
        <AddPerson title={title} formId={formConfig.formId} />,
      );
    }
    if (profile.verified && listMissingIdentifiers(profile)) {
      return wrapWithBreadcrumb(
        breadcrumbTitle,
        <Missing526Identifiers
          title={title}
          formId={formConfig.formId}
          form526RequiredIdentifiers={
            profile.claims.form526RequiredIdentifierPresence
          }
        />,
      );
    }
  }

  if (bounceToIntro) {
    return wrapWithBreadcrumb(
      breadcrumbTitle,
      <va-loading-indicator message="Please wait while we load the application for you." />,
    );
  }

  const form = (
    <ITFWrapper
      location={location}
      // ITFWrapper renders this in an h1, so resolve the BDD-aware title to a
      // string to match the h1 FormApp renders on form pages.
      title={title}
      noITFPages={NO_ITF_PAGES}
    >
      <RoutedSavableApp formConfig={formConfig} currentLocation={location}>
        {children}
      </RoutedSavableApp>
    </ITFWrapper>
  );

  // Like v1, hold the nav back until the Veteran dismisses the ITF banner,
  // and while the saved form is loading/prefilling or being saved.
  const showSideNav = isMidFormPage && itfMessageDismissed && !formLoading;

  // Keep the same tree whether or not the nav shows; swapping layouts remounts
  // RoutedSavableApp and drops its save-success redirect to /form-saved.
  const content = (
    <div className="all-claims-v2-app vads-grid-container">
      <div className="vads-grid-row">
        {showSideNav && (
          <div className="vads-u-padding-x--2p5 vads-u-padding-bottom--3 vads-grid-col-12 medium-screen:vads-grid-col-3">
            <AllClaimsV2SideNav pathname={pathname} />
          </div>
        )}
        <div
          className={`vads-u-padding-x--2p5 vads-grid-col-12${
            showSideNav ? ' medium-screen:vads-grid-col-9' : ''
          }`}
        >
          {form}
        </div>
      </div>
    </div>
  );

  return wrapWithBreadcrumb(
    breadcrumbTitle,
    <RequiredLoginView serviceRequired={SERVICE_REQUIRED} user={user} verify>
      {/* TODO: Remove before productionizing. Temporary development-only "V2" indicator, rendered only on localhost */}
      {environment.isLocalhost() && (
        <div className="dbc-v2-dev-badge" aria-hidden="true">
          V2
        </div>
      )}
      {/* TODO: Remove. Temporary isBDD troubleshooting badge, localhost only */}
      {environment.isLocalhost() && (
        <div className="dbc-v2-bdd-badge" aria-hidden="true">
          Qualifies for BDD: {String(isBDD(formData))}
        </div>
      )}
      {content}
    </RequiredLoginView>,
  );
}

AllClaimsV2App.propTypes = {
  children: PropTypes.node,
  formData: PropTypes.object,
  formLoading: PropTypes.bool,
  itfMessageDismissed: PropTypes.bool,
  location: PropTypes.object,
  router: PropTypes.shape({
    replace: PropTypes.func,
  }),
  user: PropTypes.object,
};

const mapStateToProps = state => ({
  formData: state.form?.data,
  formLoading:
    state.form?.loadedStatus === LOAD_STATUSES.pending ||
    state.form?.prefillStatus === PREFILL_STATUSES.pending ||
    state.form?.savedStatus === SAVE_STATUSES.pending,
  itfMessageDismissed: state.itf?.messageDismissed === true,
  user: state.user,
});

export default connect(mapStateToProps)(AllClaimsV2App);
